import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User, LoginCredentials } from '@/types';
import { useAuthStore } from '@/stores';
import Loading from '@/components/ui/Loading';
import { setAuthToken, setOnUnauthorized, http } from '@/services/api';
import PresenceService from '@/services/presence/PresenceService';
import { setEffectivePermissions } from '@/services/permissions/effectivePermissions';

interface AuthContextType {
  user: User | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token, isLoading, login, logout, isHydrated, loadCurrentUser } = useAuthStore();
  const navigate = useNavigate();
  const isHandlingUnauthorizedRef = useRef(false);

  // Initialize CSRF cookie for Sanctum on first mount (safe to call multiple times)
  useEffect(() => {
    // We don't await to avoid blocking initial render
    fetch('/sanctum/csrf-cookie', { credentials: 'include' }).catch(() => {
      // ignore network errors; subsequent authenticated requests can retry
    });
  }, []);

  // Initialize and sync auth token into API client
  useEffect(() => {
    setAuthToken(token ?? null);
  }, [token]);

  // Register global 401 handler to force logout and allow routing to redirect to /login
  useEffect(() => {
    setOnUnauthorized(() => {
      if (isHandlingUnauthorizedRef.current) {
        return;
      }

      isHandlingUnauthorizedRef.current = true;

      // logout will clear token/user and ProtectedRoute will redirect
      logout()
        .finally(() => {
          try {
            navigate('/login', { replace: true });
          } catch (_) {
            // Fallback for early bootstrapping
            window.location.assign('/login');
          }
        })
        .finally(() => {
          isHandlingUnauthorizedRef.current = false;
        });
    });
    return () => {
      setOnUnauthorized(undefined);
    };
  }, [logout, navigate]);

  // After store hydration, if we have a token but no user loaded yet, fetch current user
  useEffect(() => {
    if (isHydrated && token && !user) {
      void loadCurrentUser();
    }
  }, [isHydrated, token, user, loadCurrentUser]);

  // Bootstrap: fetch effective permissions snapshot once and cache it
  useEffect(() => {
    if (!isHydrated || !token) return;
    // Require authenticated user to exist to key the cache; if not yet available, we'll try again on next effect run
    if (!user) return;

    const cacheKey = `permSnap:${user.id}`;
    try {
      const cachedRaw = localStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw) as { version: string; checksum: string; perms: Array<{ resource: string; action: string; scope: 'own' | 'team' | 'region' | 'all' }> };
        // If our in-memory store is empty or outdated, hydrate it immediately from cache for snappy UI
        if (cached && Array.isArray(cached.perms)) {
          setEffectivePermissions(cached.perms, cached.version, cached.checksum);
        }
      }
    } catch {
      // ignore cache parse errors
    }

    // Always attempt a conditional fetch to respect ETag and keep it fresh
    (async () => {
      try {
        type BootstrapResp = {
          user: User & { team_ids?: number[] };
          effectivePermissions: Array<{ resource: string; action: string; scope: 'own' | 'team' | 'region' | 'all' }>;
          permissionVersion: string;
          permissionChecksum: string;
        };
        const data = await http.get<BootstrapResp>('/bootstrap');
        if (data && data.effectivePermissions) {
          setEffectivePermissions(data.effectivePermissions, data.permissionVersion, data.permissionChecksum);
          try {
            localStorage.setItem(cacheKey, JSON.stringify({
              version: data.permissionVersion,
              checksum: data.permissionChecksum,
              perms: data.effectivePermissions,
            }));
          } catch {
            // storage may be full or unavailable; ignore
          }
        }
      } catch {
        // ignore bootstrap errors; app can still function with cached/empty permissions and server-side policies
      }
    })();
  }, [isHydrated, token, user]);

  // Heartbeat: while authenticated, periodically update presence (last_seen)
  useEffect(() => {
    if (!token) return; // Not authenticated

    const intervalId = window.setInterval(() => {
      void sendHeartbeat();
    }, 60000);

    const sendHeartbeat = async () => {
      try {
        await PresenceService.heartbeat();
      } catch (e) {
        // Avoid noisy logs; heartbeat failures are not critical
      }
    };

    // Initial heartbeat soon after mount
    void sendHeartbeat();

    // Extra: on window focus, refresh presence quickly
    const onFocus = () => { void sendHeartbeat(); };
    window.addEventListener('focus', onFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [token]);

  const value = {
    user,
    login,
    logout,
    loading: isLoading || !isHydrated, // App is loading if API is loading OR store is not hydrated
    isAuthenticated: Boolean(token),
  };

  // Wait for the auth store to be hydrated before rendering the app
  if (!isHydrated) {
    return <Loading fullScreen text="Iniciando..." />;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
